import { IonGrid, IonRow, IonCol, IonIcon, IonRange, IonLabel, isPlatform, IonPopover, IonList, IonItem, IonListHeader, IonToast, IonSpinner } from '@ionic/react';
import React, { useState, useRef, useEffect, Fragment, useContext, forwardRef, useImperativeHandle } from 'react';
import './BSTAudioPlayer.scss';
import { play, pause, ellipsisVertical, checkmark } from 'ionicons/icons';
import BibleSettingsService from '../../services/BibleSettingsService';
import IBibleSettings from '../../interfaces/IBibleSettings';
import NetworkStatusContext from '../NetworkStatusContext/NetworkStatusContext';
import AudioTimeCodeService, { IAudioTimeCode } from '../../services/AudioTimeCodeService';
import NoSleepContext from '../NoSleepContext/NoSleepContext';
import { InsomniaOriginal } from '@awesome-cordova-plugins/insomnia';
import NoSleep from 'nosleep.js';

interface IBSTAudioPlayerProps {
    audioUrl: string,
    showPlayer: boolean,
    startPlaying: boolean,
    initializedSpeed: number,
    onComplete: Function,
    onVerseChange: (verseId: string) => void,
    stayAwake: boolean,
}

export interface IRemotePlayPauseAudio {
    remotePlayPauseAudio: Function,
    remoteResetPlayer: Function,
}

type playerStatusType = 'playing' | 'paused' | 'loading' | 'deactivated' | 'hidden';

const BSTAudioPlayer = forwardRef<IRemotePlayPauseAudio, IBSTAudioPlayerProps>((props, ref) => {
    const NetworkService = useContext(NetworkStatusContext);
    const [showNoNetworkConnectionMessage, setShowNoNetworkConnectionMessage] = useState(false);
    const [showLoadingAudioError, setShowLoadingAudioError] = useState(false);
    const [showSlowNetworkMessage, setShowSlowNetworkMessage] = useState(false);
    const [audioButtonIcon, setAudioButtonIcon] = useState<string>(pause);
    const [playerStatus, setPlayerStatus] = useState<playerStatusType>('deactivated');
    const [playerRangePosition, setPlayerRangePosition] = useState<number>(0);
    const [playerRangeMax, setPlayerRangeMax] = useState<number>(10000);
    const [loadingDuration, setLoadingDuration] = useState<string>('LOADING');
    const [playBackSpeed, setPlayBackSpeed] = useState<number>(props.initializedSpeed);
    const [showSpeedOptions, setShowSpeedOptions] = useState<boolean>(false);
    const [showSpeedOptionsEvent, setShowSpeedOptionsEvent] = useState<Event | undefined>(undefined);
    const [activeBibleSettings, setActiveBibleSettings] = useState<IBibleSettings | undefined>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const BibleTextAudioFile = useRef<HTMLAudioElement | undefined>(undefined);
    const playerPosition = useRef<number>(0);
    const changedPosition = useRef<number>(0);
    const audioDuration = useRef<number>(0);
    const playerPositionChanged = useRef<boolean>(false);
    const fileLoaded = useRef<boolean>(false);
    const timerId = useRef(0);
    const slowNetworkTimerId = useRef(0);
    const slowNetworkWaiting = useRef<boolean>(false);
    const playBackInterval = useRef<number>(props.initializedSpeed * 1000);
    const initializedSpeedRef = useRef<number>(props.initializedSpeed);
    const playBackSpeedRef = useRef<number>(playBackSpeed);
    const playerStatusRef = useRef<string>(playerStatus);
    const timeCodeJsonData = useRef<IAudioTimeCode | undefined>(undefined);
    const currentVerseId = useRef<string>('');
    const noSleep = useContext(NoSleepContext);
    const noSleepActive = useRef(false);
    if (isPlatform('hybrid')) {
    } else {
    } 

    const enableNoSleep = () => {
        // props.stayAwake enables/disables feature
        if(props.stayAwake && !noSleepActive.current) {
            noSleepActive.current = true;
            if(isPlatform('hybrid')) {
                const awake = noSleep as InsomniaOriginal;
                awake?.keepAwake();
            } else if (!isPlatform('ios')) {
                const awake = noSleep as NoSleep;
                awake?.enable();
            } 
        }
    }

    const disableNoSleep = () => {
        if(props.stayAwake && noSleepActive.current) {
            noSleepActive.current = false;
            if(isPlatform('hybrid')) {
                const awake = noSleep as InsomniaOriginal;
                awake?.allowSleepAgain();
            } else if (!isPlatform('ios')) {
                const awake = noSleep as NoSleep;
                awake?.disable();
            } 
        }
    }

    useEffect(() => {
        return () => {
            window.clearInterval(timerId.current);
            window.clearTimeout(slowNetworkTimerId.current);
            disableNoSleep();
            if(BibleTextAudioFile.current) {
                console.log('Audio Bible Closed');
                (BibleTextAudioFile.current as HTMLAudioElement).pause();
            }
        }
    }, [props.audioUrl]);

    useEffect(() => {
        initializedSpeedRef.current = props.initializedSpeed;
        playerStatusRef.current = playerStatus;
        playBackSpeedRef.current = playBackSpeed;
    }, [props.initializedSpeed, playerStatus, playBackSpeed]);

    const initPlayer = () => {
        audioDuration.current = 0;
        fileLoaded.current = false;
        setIsLoading(true);
        setLoadingDuration('LOADING');

        AudioTimeCodeService.getAudioTimeCode(props.audioUrl).then(timeCodeData => {
            timeCodeJsonData.current = timeCodeData;
            console.log(timeCodeData);
        });

        if(BibleTextAudioFile.current) {
            console.log('Updated Audio File: ', props.audioUrl);
            BibleTextAudioFile.current.src = props.audioUrl;
            BibleTextAudioFile.current.load();
        } else {
            BibleTextAudioFile.current = new Audio(props.audioUrl);
            BibleTextAudioFile.current.playbackRate = playBackSpeed ?? props.initializedSpeed;

            BibleTextAudioFile.current.onplaying = (e => {
                console.log('playing');
                window.clearTimeout(slowNetworkTimerId.current);
                slowNetworkWaiting.current = false;
                setShowSlowNetworkMessage(false);
                setIsLoading(false);
            });
            playPauseAudio();
            
            BibleTextAudioFile.current.onloadeddata = () => {
                (BibleTextAudioFile.current as HTMLAudioElement).playbackRate = playBackSpeedRef.current ?? initializedSpeedRef.current;
                fileLoaded.current = true;
                setIsLoading(false); 
                console.log('Audio File Loaded');
                if (playerStatusRef.current !== 'playing') {
                    playPauseAudio();
                }
                if(BibleTextAudioFile.current) {
                    BibleTextAudioFile.current.onwaiting = (e => {
                        console.log('waiting');
                        slowNetworkTimerId.current = window.setTimeout(()=> {
                            setShowSlowNetworkMessage(true);
                        }, 1000);
                        slowNetworkWaiting.current = true;
                        setIsLoading(true);
                    });
                }
            };
            BibleTextAudioFile.current.onerror = (event) => {
                setShowLoadingAudioError(true);
                console.error("Audio Error:", (BibleTextAudioFile.current as HTMLAudioElement).error, event);
            };
            BibleTextAudioFile.current.onloadedmetadata = (event) => {
                console.log("MetaData", event, BibleTextAudioFile.current?.mediaKeys);
            };
        }
    }

    const resetPlayer = () => {
        setAudioButtonIcon(pause);
        setPlayerStatus('deactivated');
        playerPosition.current = 0;
        changedPosition.current = 0;
        playerPositionChanged.current = false;
        setPlayerRangePosition(0);
        changedPosition.current = 0;
        disableNoSleep();
    }

    useImperativeHandle(ref, () => ({
        remotePlayPauseAudio() {
            resetPlayer();
            initPlayer();
        },
        remoteResetPlayer() {
            resetPlayer();
            pauseAudio();
        },
    }));

    const formatTime = (time: number) => {
        const hours = Math.floor((time % (86400000)) / (3600000));
        const minutes = Math.floor((time % (3600000)) / (60000));
        const seconds = Math.floor((time % (60000)) / 1000);
        
        const displayHours = (hours < 10) ? "0" + hours : hours;
        const displayMinutes = (minutes < 10) ? "0" + minutes : minutes;
        const displaySeconds = (seconds < 10) ? "0" + seconds : seconds;
    
        let displayTime = `${displayMinutes}:${displaySeconds}`;
        displayTime = hours ? `${displayHours}:${displayTime}`: displayTime;
        return displayTime;
    }

    const startPlaying = () => {
        window.clearInterval(timerId.current);
        timerId.current = window.setInterval(() => {
            if(audioDuration.current === 0) {
                let audioSeconds = 0;
                audioSeconds = BibleTextAudioFile.current ? (BibleTextAudioFile.current as HTMLAudioElement).duration : 0;
                audioDuration.current = (audioSeconds > 0 ? Math.floor(audioSeconds) : 0) * 1000;
                if(!!audioDuration.current && fileLoaded.current) {
                    setPlayerRangeMax(audioDuration.current);
                    setLoadingDuration('');
                    console.log('audio duration: ', audioDuration.current);
                }
            }

            if(!!audioDuration.current && fileLoaded.current) {
                // Change the location in player audio file
                if(playerPositionChanged.current 
                    && playerPosition.current < audioDuration.current) {
                    console.log('change accepted', changedPosition.current, playerPosition.current);
                    playerPositionChanged.current = false;
                    playerPosition.current = changedPosition.current;
                    setPlayerRangePosition(playerPosition.current);
                    (BibleTextAudioFile.current as HTMLAudioElement).currentTime = playerPosition.current / 1000;
                } else {
                    if (slowNetworkWaiting.current) {
                        // Do not move the range slider because the audio is paused
                    } 
                    // Move the range slider one interval forward and send notice about verse change
                    else if (playerPosition.current < audioDuration.current) {
                        playerPosition.current = playerPosition.current + playBackInterval.current;
                        changedPosition.current = playerPosition.current;
                        setPlayerRangePosition(playerPosition.current);
                        if(timeCodeJsonData.current) {
                            const currentTimeCode = timeCodeJsonData.current.timeCodes.find(timeCode => 
                                timeCode.start < playerPosition.current && 
                                timeCode.end > playerPosition.current);
                            if(currentTimeCode?.id && currentTimeCode?.id !== currentVerseId.current) {
                                currentVerseId.current = currentTimeCode?.id;
                                props.onVerseChange(currentTimeCode?.id);
                            }
                        }
                        console.log('new audio position', playerPosition.current);
                    } 
                    // Make sure player is done with audio by letting the range slider go one extra interval
                    else if (playerPosition.current < (audioDuration.current + playBackInterval.current)) {
                        playerPosition.current = playerPosition.current + playBackInterval.current;
                        changedPosition.current = playerPosition.current;
                        console.log('last audio position', playerPosition.current);
                    } 
                    // Player Done reset player
                    else {
                        changedPosition.current = 0;
                        playerPosition.current = 0;
                        setPlayerRangePosition(0);
                        setAudioButtonIcon(play);
                        setPlayerStatus('paused');
                        disableNoSleep();
                        (BibleTextAudioFile.current as HTMLAudioElement)?.pause();
                        (BibleTextAudioFile.current as HTMLAudioElement).currentTime = 0;
                        props.onComplete();
                        window.clearInterval(timerId.current);
                    }
                }
            }                
        }, 1000);
    }

    const pauseAudio = () => {
        setAudioButtonIcon(play);
        setPlayerStatus('paused');
        disableNoSleep();
        console.log('paused');
        BibleTextAudioFile.current?.pause();
        window.clearInterval(timerId.current);
    }

    const playPauseAudio = () => {
        console.log('Play/Pause Pressed', playerStatus, isLoading);

        switch (playerStatus) {
            case 'playing':
                pauseAudio();
                break;
            case 'paused':
                if(NetworkService?.isListening && NetworkService.networkConnected) {
                    setAudioButtonIcon(pause);
                    setPlayerStatus('playing');
                    enableNoSleep();
                    console.log('continue playing');
                    BibleTextAudioFile.current?.play();
                    startPlaying();
                } else {
                    setShowNoNetworkConnectionMessage(true);
                }
                
                break;
            case 'loading':
                if(NetworkService?.isListening && NetworkService.networkConnected) {
                    setAudioButtonIcon(pause);
                    setPlayerStatus('playing');
                } else {
                    setShowNoNetworkConnectionMessage(true);
                }
                break;
            case 'deactivated':
                if(NetworkService?.isListening && NetworkService.networkConnected) {
                    setAudioButtonIcon(pause);
                    setPlayerStatus('playing');
                    enableNoSleep();
                    console.log('start playing');
                    BibleTextAudioFile.current?.play();
                    setPlayerRangeMax(audioDuration.current);
                    startPlaying();
                } else {
                    setShowNoNetworkConnectionMessage(true);
                }
                break;
            case 'hidden':
                setAudioButtonIcon(play);
                break;
        }
    }

    const changePlayerPosition = (oldPosition: number , newPosition: number) => {
        console.log('changed', oldPosition, newPosition);
        if(oldPosition !== newPosition && newPosition <= audioDuration.current) {
            playerPositionChanged.current = true;
            changedPosition.current = newPosition;
            console.log('*****', oldPosition, newPosition);
        }
    }

    const updatePlayBackSpeed = (newSpeed: number) => {
        setPlayBackSpeed(newSpeed);
        playBackInterval.current = newSpeed * 1000;
        setShowSpeedOptions(false);
        (BibleTextAudioFile.current as HTMLAudioElement).playbackRate = newSpeed;
        if(activeBibleSettings){
            const updatedBibleSettings = {...activeBibleSettings, audioSpeed: newSpeed}
            BibleSettingsService.updateBibleSettings(updatedBibleSettings).then(result => {
                if(result.Success) {
                    setActiveBibleSettings(result.Data!);
                }
            });
        } else {
        BibleSettingsService.getBibleSettings().then(bibleSettingsResponse => {
            if(bibleSettingsResponse.Success) {
              setActiveBibleSettings(bibleSettingsResponse.Data!);
              const updatedBibleSettings = {...bibleSettingsResponse.Data!, audioSpeed: newSpeed}
              BibleSettingsService.updateBibleSettings(updatedBibleSettings).then(result => {
                  if(result.Success) {
                    setActiveBibleSettings(result.Data!);
                  }
              });
            }
          }); 
        }
    }

    // We could make another minified round player here and then control which one is visible via props.
    // while scrolling down I think this would be a better player, while scrolling up or at the start a minified round player would be better.
    return (
        <Fragment>
        <IonGrid className={props.showPlayer ? 'bst-audio-player-container' : 'bst-audio-player-container bst-audio-player-hide'}>
            <IonRow>
                <IonCol className="bst-audio-player-button" onClick={playPauseAudio}>
                    { isLoading ? 
                        <IonSpinner name="dots"></IonSpinner> :
                        <IonIcon icon={audioButtonIcon} /> 
                    }
                </IonCol>
                <IonCol>
                    <IonRange mode="md" className="bst-audio-player-range" 
                        value={playerRangePosition} min={0} max={playerRangeMax}
                        onIonKnobMoveEnd={e => changePlayerPosition(playerRangePosition, +e.detail.value?.toString())}>
                    <IonLabel slot="start">{formatTime(playerRangePosition / playBackSpeed)}</IonLabel>
                    <IonLabel slot="end">{loadingDuration ? loadingDuration : formatTime(playerRangeMax / playBackSpeed)}</IonLabel>
                    </IonRange>
                </IonCol>
                <IonCol className="bst-audio-player-button">
                    <IonIcon icon={ellipsisVertical} onClick={e => {
                            setShowSpeedOptions(true);
                            setShowSpeedOptionsEvent(e.nativeEvent);
                        }}/>
                </IonCol>
            </IonRow>
        </IonGrid>
        <div className={props.showPlayer ? 'bst-ios-save-zone' : 'bst-ios-save-zone bst-audio-player-hide'}></div>
        <IonPopover
            className='bst-audio-player-menu-popover'
            mode="md"
            isOpen={showSpeedOptions}
            event={showSpeedOptionsEvent}
            onDidDismiss={() => {
                setShowSpeedOptions(false); 
                setShowSpeedOptionsEvent(undefined);
            }}
        >
            <IonList className="bst-audio-player-menu">
                <IonListHeader lines="inset">
                    <IonLabel class="audio-player-menu-header">PLAYBACK SPEED</IonLabel>
                </IonListHeader>
                <IonItem onClick={()=> updatePlayBackSpeed(2)}>
                    <IonLabel>x 2</IonLabel>
                    {playBackSpeed === 2 && <IonIcon slot="end" icon={checkmark}></IonIcon> } 
                </IonItem>
                <IonItem onClick={()=> updatePlayBackSpeed(1.75)}>
                    <IonLabel>x 1.75</IonLabel>
                    {playBackSpeed === 1.75 && <IonIcon slot="end" icon={checkmark}></IonIcon> }
                </IonItem>
                <IonItem onClick={()=> updatePlayBackSpeed(1.5)}>
                    <IonLabel>x 1.5</IonLabel>
                    {playBackSpeed === 1.5 && <IonIcon slot="end" icon={checkmark}></IonIcon> }
                </IonItem>
                <IonItem onClick={()=> updatePlayBackSpeed(1.25)}>
                    <IonLabel>x 1.25</IonLabel>
                    {playBackSpeed === 1.25 && <IonIcon slot="end" icon={checkmark}></IonIcon> }
                </IonItem>
                <IonItem onClick={()=> updatePlayBackSpeed(1)}>
                    <IonLabel>x 1</IonLabel>
                    {playBackSpeed === 1 && <IonIcon slot="end" icon={checkmark}></IonIcon> }
                </IonItem>
                <IonItem onClick={()=> updatePlayBackSpeed(.75)}>
                    <IonLabel>x .75</IonLabel>
                    {playBackSpeed === .75 && <IonIcon slot="end" icon={checkmark}></IonIcon> }
                </IonItem>
            </IonList>
        </IonPopover>
        <IonToast color="danger"
              isOpen={showSlowNetworkMessage}
              onDidDismiss={() => setShowSlowNetworkMessage(false)}
              message={"Slow Network Detected: Audio Loading"}
              duration={3000} />
        <IonToast color="danger"
              isOpen={showNoNetworkConnectionMessage}
              onDidDismiss={() => setShowNoNetworkConnectionMessage(false)}
              message={"NO INTERNET CONNECTION DETECTED - Connection is required to play audio."}
              duration={3000} />
        <IonToast color="danger"
              isOpen={showLoadingAudioError}
              onDidDismiss={() => setShowLoadingAudioError(false)}
              message={"Error Loading Audio: Please report this Bible version and Day to the developer."}
              duration={6000} />
        </Fragment>
    );
});

export default BSTAudioPlayer;